import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Award,
  Target,
  TrendingUp,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useJobMatchPrerequisites } from '../hooks/useJobMatchPrerequisites';
import { cn } from '@shared/utils/cn';

interface JobMatchPrerequisitesCheckerProps {
  onPrerequisitesMet?: () => void;
  showCompactView?: boolean;
}

export const JobMatchPrerequisitesChecker: React.FC<JobMatchPrerequisitesCheckerProps> = ({
  onPrerequisitesMet,
  showCompactView = false
}) => {
  const navigate = useNavigate();
  const { prerequisites, isLoading, error, checkPrerequisites, canAccessJobMatch } = useJobMatchPrerequisites();

  React.useEffect(() => {
    if (canAccessJobMatch && onPrerequisitesMet) {
      onPrerequisitesMet();
    }
  }, [canAccessJobMatch, onPrerequisitesMet]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-gray-600">Checking prerequisites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900 mb-1">Error Checking Prerequisites</h3>
            <p className="text-sm text-red-700 mb-3">{error}</p>
            <Button onClick={checkPrerequisites} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!prerequisites) {
    return null;
  }

  if (canAccessJobMatch) {
    if (showCompactView) {
      return (
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-900">
              All prerequisites complete
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  const prerequisiteItems = [
    {
      id: 'resume',
      label: 'Resume',
      description: 'Upload your resume with extracted content',
      completed: prerequisites.has_resume,
      icon: FileText,
      action: () => navigate('/my-profile'),
      actionLabel: 'Upload Resume',
      metadata: prerequisites.resume_quality_score > 0 ? `Quality: ${prerequisites.resume_quality_score}%` : null
    },
    {
      id: 'brag',
      label: 'BRAG Sheet',
      description: 'Complete at least 5 achievements',
      completed: prerequisites.has_brag_sheet,
      icon: Award,
      action: () => navigate('/dashboard'),
      actionLabel: 'Complete BRAG Sheet',
      metadata: `${prerequisites.brag_items_count} of 5 items completed`
    },
    {
      id: 'dream',
      label: 'DREAM Sheet',
      description: 'Complete at least 5 career preferences',
      completed: prerequisites.has_dream_sheet,
      icon: Target,
      action: () => navigate('/dashboard'),
      actionLabel: 'Complete DREAM Sheet',
      metadata: `${prerequisites.dream_items_count} of 5 items completed`
    },
    {
      id: 'report',
      label: 'Career Intelligence Report',
      description: 'Generate your comprehensive career analysis',
      completed: prerequisites.has_career_report,
      icon: TrendingUp,
      action: () => navigate('/reports'),
      actionLabel: 'Generate Report',
      metadata: null
    }
  ];

  return (
    <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-yellow-100 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-yellow-900 mb-1">
            Complete Prerequisites to Access Job Match AI
          </h3>
          <p className="text-sm text-yellow-700 mb-4">
            Job Match AI requires your foundational career data to provide accurate analysis.
            Please complete the following steps:
          </p>

          <div className="space-y-3">
            {prerequisiteItems.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-start gap-3 p-4 rounded-lg border',
                    item.completed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-gray-200'
                  )}
                >
                  <div className={cn(
                    'p-2 rounded-lg mt-0.5',
                    item.completed ? 'bg-green-100' : 'bg-gray-100'
                  )}>
                    {item.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <Icon className="h-5 w-5 text-gray-600" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={cn(
                        'font-medium',
                        item.completed ? 'text-green-900' : 'text-gray-900'
                      )}>
                        {item.label}
                      </h4>
                      {item.metadata && (
                        <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                          {item.metadata}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {item.description}
                    </p>
                    {!item.completed && (
                      <Button
                        onClick={item.action}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        {item.actionLabel}
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {prerequisites.next_steps && prerequisites.next_steps.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Recommended Next Steps</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                {prerequisites.next_steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-yellow-200">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-yellow-900">
            {prerequisiteItems.filter(item => item.completed).length} of {prerequisiteItems.length} completed
          </div>
          <div className="flex gap-1">
            {prerequisiteItems.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'h-2 w-8 rounded-full',
                  item.completed ? 'bg-green-500' : 'bg-gray-300'
                )}
              />
            ))}
          </div>
        </div>
        <Button onClick={checkPrerequisites} variant="ghost" size="sm">
          Refresh Status
        </Button>
      </div>
    </div>
  );
};

export default JobMatchPrerequisitesChecker;
